import React, { useState, useEffect, useCallback } from "react";
import {
  LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell, PieChart, Pie, Legend
} from "recharts";
import {
  CalendarDays, CalendarRange, ShoppingBasket, TrendingUp,
  Wallet, Receipt, Clock3, Trophy, ClipboardList, Info,
  ChartColumnIncreasing,
} from "lucide-react";
import { host } from "../script/variables";
import "../style/financeStats.css";

// ─── IST helpers ──────────────────────────────────────────────────────────────
// All UTC dates from the DB are shifted to IST (+5:30) before any display
// or date-bucketing logic. We never use new Date() raw for comparisons.

const IST_OFFSET_MS = 5.5 * 60 * 60 * 1000; // 330 minutes in ms

/**
 * Convert a UTC Date object (or ISO string) to its IST equivalent Date.
 * The returned Date's local methods (getHours, getDate, etc.) reflect IST.
 */
const toIST = (utcInput) => {
  const utcMs = new Date(utcInput).getTime();
  return new Date(utcMs + IST_OFFSET_MS);
};

/**
 * Return today's date string in IST (YYYY-MM-DD).
 */
const todayIST = () => {
  const ist = toIST(new Date());
  return fmtDate(ist);
};

/**
 * Format a Date to YYYY-MM-DD (uses UTC methods since we already shifted).
 */
const fmtDate = (d) => d.toISOString().slice(0, 10);

/**
 * Group an array of orders/events by IST hour.
 * Each item must have a `placedAt` (UTC ISO string) and a numeric `totalPrice`.
 * Returns array of { time: "09:00", revenue, orders } sorted by hour.
 */
const buildHourlyTimeline = (orders = []) => {
  const buckets = {};
  for (const order of orders) {
    if (!order.placedAt) continue;
    const ist  = toIST(order.placedAt);
    const hour = ist.getUTCHours(); // safe: we already shifted to IST via toIST
    const key  = `${String(hour).padStart(2, "0")}:00`;
    if (!buckets[key]) buckets[key] = { time: key, revenue: 0, orders: 0 };
    buckets[key].revenue += Number(order.totalPrice || 0);
    buckets[key].orders  += 1;
  }
  return Object.values(buckets).sort((a, b) => a.time.localeCompare(b.time));
};

// ─── other helpers ────────────────────────────────────────────────────────────
const fmt = (n) => Number(n || 0).toLocaleString("en-IN");

const yesterdayIST = () => {
  const ist = toIST(new Date());
  ist.setUTCDate(ist.getUTCDate() - 1);
  return fmtDate(ist);
};

const thisWeekStartIST = () => {
  const ist = toIST(new Date());
  // Sunday = 0 … Saturday = 6  (getUTCDay is correct here since ist is shifted)
  ist.setUTCDate(ist.getUTCDate() - ist.getUTCDay());
  return fmtDate(ist);
};

const thisMonthStartIST = () => {
  const ist = toIST(new Date());
  return `${ist.getUTCFullYear()}-${String(ist.getUTCMonth() + 1).padStart(2, "0")}-01`;
};

/**
 * When the user picks a date string like "2024-07-15" in IST and we need to
 * send UTC window to the backend, convert the IST day boundaries to UTC ISO.
 *
 *   IST day start = 00:00 IST = previous day 18:30 UTC
 *   IST day end   = 23:59:59 IST = same day 18:29:59 UTC
 *
 * The backend should ideally accept IST date strings directly, but if it
 * filters by UTC timestamps, pass these computed UTC bounds.
 */
const istDayToUTCRange = (istDateStr) => {
  // "2024-07-15" → IST midnight = UTC 2024-07-14T18:30:00Z
  const startUTC = new Date(`${istDateStr}T00:00:00+05:30`).toISOString();
  const endUTC   = new Date(`${istDateStr}T23:59:59+05:30`).toISOString();
  return { startUTC, endUTC };
};

const CHART_COLORS = [
  "#6366f1", "#f59e0b", "#10b981", "#ef4444",
  "#3b82f6", "#ec4899", "#14b8a6", "#f97316",
];

// Formats "09:00" style hour keys into a readable 12-hour label
const hourLabel = (t) => {
  const [h] = t.split(":");
  const hr  = parseInt(h, 10);
  return hr === 0 ? "12 AM"
    : hr < 12   ? `${hr} AM`
    : hr === 12 ? "12 PM"
    : `${hr - 12} PM`;
};

// ─── sub-components ───────────────────────────────────────────────────────────
const KpiCard = ({ icon: Icon, label, value, sub, tone = "default" }) => (
  <div className={`kpi-card kpi-card--${tone}`}>
    <div className="kpi-icon"><Icon size={18} strokeWidth={2.2} /></div>
    <div className="kpi-body">
      <p className="kpi-label">{label}</p>
      <h4 className="kpi-value">{value}</h4>
      {sub && <span className="kpi-sub">{sub}</span>}
    </div>
  </div>
);

const SectionHeader = ({ icon: Icon, title, note }) => (
  <div className="section-header">
    <span className="section-icon"><Icon size={18} strokeWidth={2.2} /></span>
    <div>
      <h3>{title}</h3>
      {note && <p className="section-note">{note}</p>}
    </div>
  </div>
);

// A small "?" style hint bubble — used to explain jargon like GST in plain words
const InfoHint = ({ text }) => (
  <span className="info-hint" tabIndex={0} title={text}>
    <Info size={13} strokeWidth={2.4} />
  </span>
);

// Horizontal ranked bar table for top items
const RankedTable = ({ rows, valueKey = "revenue", label = "Revenue" }) => {
  if (!rows || rows.length === 0) return <p className="empty-state">No sales yet for this period.</p>;
  const max = Math.max(...rows.map((r) => r[valueKey]));
  return (
    <div className="ranked-table">
      <div className="ranked-table-header">
        <span>Item</span>
        <span>Qty</span>
        <span>{label}</span>
      </div>
      {rows.map((row, i) => (
        <div className="ranked-row" key={row.name || i}>
          <div className="ranked-name-col">
            <span className="rank-badge">{i + 1}</span>
            <div className="ranked-bar-wrap">
              <span className="ranked-name">{row.name}</span>
              <div className="ranked-bar">
                <div
                  className="ranked-bar-fill"
                  style={{
                    width: `${Math.max(4, (row[valueKey] / max) * 100)}%`,
                    backgroundColor: CHART_COLORS[i % CHART_COLORS.length],
                  }}
                />
              </div>
            </div>
          </div>
          <span className="ranked-qty">{row.qty}</span>
          <span className="ranked-revenue">₹ {fmt(row[valueKey])}</span>
        </div>
      ))}
    </div>
  );
};

// ─── main component ───────────────────────────────────────────────────────────
const FinanceStats = ({ cafeid }) => {
  const [data, setData]               = useState(null);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState("");
  const [activeTab, setActiveTab]     = useState("day");
  const [selectedItem, setSelectedItem] = useState("");

  // ── date filter — same model as the Bank Settlement tab: pick a period
  // chip, and only reveal raw date inputs when "Custom" is chosen.
  const [period, setPeriod]   = useState("today");
  const [fromDate, setFromDate] = useState(todayIST());
  const [toDate, setToDate]     = useState(todayIST());
  // The "day" tab always reflects the last day of whatever range is active.
  const dayInView = toDate;

  const PERIODS = [
    { key: "today",     label: "Today" },
    { key: "yesterday", label: "Yesterday" },
    { key: "thisWeek",  label: "This Week" },
    { key: "thisMonth", label: "This Month" },
    { key: "custom",    label: "Custom" },
  ];

  // Every non-custom preset resolves through this one function, exactly
  // like the Bank Settlement tab's period chips.
  const computePeriodRange = (key) => {
    switch (key) {
      case "today": {
        const t = todayIST();
        return { from: t, to: t };
      }
      case "yesterday": {
        const y = yesterdayIST();
        return { from: y, to: y };
      }
      case "thisWeek":
        return { from: thisWeekStartIST(), to: todayIST() };
      case "thisMonth":
        return { from: thisMonthStartIST(), to: todayIST() };
      default:
        return null;
    }
  };

  const handlePeriod = (key) => {
    setPeriod(key);
    if (key === "custom") return; // wait for the user to pick From/To
    const r = computePeriodRange(key);
    if (r) { setFromDate(r.from); setToDate(r.to); }
  };

  // ── fetch ───────────────────────────────────────────────────────────────────
  const fetchStats = useCallback(async () => {
    if (new Date(fromDate) > new Date(toDate)) {
      setError("Your 'From' date is after your 'To' date — please fix the range.");
      return;
    }
    try {
      setLoading(true);
      setError("");
      const token = localStorage.getItem("partnertoken");

      // Convert IST date strings to UTC ISO boundaries for the backend query.
      // The backend stores timestamps in UTC, so we pass UTC range equivalents.
      const { startUTC: selectedStart, endUTC: selectedEnd } = istDayToUTCRange(dayInView);
      const { startUTC: fromUTC }                             = istDayToUTCRange(fromDate);
      const { endUTC:   toUTC }                               = istDayToUTCRange(toDate);

      const res = await fetch(`${host}/res_stats/get_revenue_stats`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({
          restaurantId:  cafeid,
          // IST date strings (for any backend logic that uses date strings)
          selectedDate: dayInView,
          fromDate,
          toDate,
          // UTC ISO boundaries (for any backend timestamp range queries)
          selectedStartUTC: selectedStart,
          selectedEndUTC:   selectedEnd,
          fromUTC,
          toUTC,
          // Let the backend know the client's timezone
          timezone: "Asia/Kolkata",
        }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.message || "Failed");
      setData(json);
    } catch (err) {
      setError(err.message || "Couldn't load your sales data. Please check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, [cafeid, dayInView, fromDate, toDate]);

  useEffect(() => { fetchStats(); }, [fetchStats]);

  // ── derived ─────────────────────────────────────────────────────────────────
  const periodSummary  = data?.periodAnalytics?.summary;
  const daySummary     = data?.dayAnalytics?.summary;
  const sb             = data?.salesBreakdown;
  const allItemNames   = sb ? Object.keys(sb.itemDailyTrend || {}).sort() : [];
  const trendData      = sb && selectedItem ? (sb.itemDailyTrend[selectedItem] || []) : [];
  const trendItemTotal = trendData.reduce(
    (s, d) => ({ qty: s.qty + d.qty, revenue: s.revenue + d.revenue }),
    { qty: 0, revenue: 0 }
  );

  // If the backend returns raw order records for the day timeline, build
  // the hourly buckets in IST here on the frontend. Backend already returns
  // IST hours when pre-bucketed, so that shape is used as-is.
  const revenueTimeline = (() => {
    const raw = data?.dayAnalytics?.revenueTimeline;
    if (!raw || !raw.length) return [];

    // Case A: pre-bucketed { time, revenue, orders } — use directly
    if (raw[0]?.time !== undefined && raw[0]?.revenue !== undefined) {
      return [...raw].sort((a, b) => a.time.localeCompare(b.time));
    }

    // Case B: raw order records with placedAt — build IST buckets client-side
    if (raw[0]?.placedAt !== undefined) {
      return buildHourlyTimeline(raw);
    }

    return raw;
  })();

  // Prefer a pre-converted IST date from backend; fall back to the date field
  const dailyComparison = (data?.periodAnalytics?.dailyComparison || []).map((row) => ({
    ...row,
    date: row.dateIST || row.date,
  }));

  const tabs = [
    { key: "day",    label: "Today's View",   Icon: CalendarDays },
    { key: "period", label: "Date Range",      Icon: CalendarRange },
    { key: "sales",  label: "What Sold",       Icon: ShoppingBasket },
    { key: "trend",  label: "Item History",    Icon: TrendingUp },
  ];

  return (
    <div className="finance-container">
      {/* ── Header — matches Bank Settlement tab for a consistent look ── */}
      <div className="fs-header">
        <div className="fs-header-eyebrow">
          <ChartColumnIncreasing size={16} strokeWidth={2.2} />
          <span>Sales &amp; Trends</span>
        </div>
        <p className="fs-page-subtitle">Revenue, orders and top-selling items for the period you choose</p>
      </div>

      {/* ── Quick Select Buttons ── */}
      <div className="fs-filter-row" role="group" aria-label="Time period">
        {PERIODS.map((p) => (
          <button
            key={p.key}
            type="button"
            className={`fs-chip ${period === p.key ? "fs-chip--active" : ""}`}
            aria-pressed={period === p.key}
            onClick={() => handlePeriod(p.key)}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* ── Custom date range — only shown when "Custom" is picked ── */}
      {period === "custom" && (
        <div className="fs-date-row">
          <div className="fs-date-field">
            <label className="fs-date-label" htmlFor="fs-from-date">From</label>
            <input
              id="fs-from-date"
              type="date"
              value={fromDate}
              max={toDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="fs-date-input"
            />
          </div>
          <div className="fs-date-sep">→</div>
          <div className="fs-date-field">
            <label className="fs-date-label" htmlFor="fs-to-date">To</label>
            <input
              id="fs-to-date"
              type="date"
              value={toDate}
              max={todayIST()}
              onChange={(e) => setToDate(e.target.value)}
              className="fs-date-input"
            />
          </div>
        </div>
      )}

      {error && <p className="error-text">{error}</p>}
      {loading && (
        <div className="loading-wrap">
          <div className="spinner" />
          <p>Fetching your sales data…</p>
        </div>
      )}

      {data && !loading && (
        <>
          {/* ── Tab Nav ── */}
          <div className="tab-nav" role="tablist" aria-label="Sales report sections">
            {tabs.map((t) => (
              <button
                key={t.key}
                role="tab"
                aria-selected={activeTab === t.key}
                className={`tab-btn ${activeTab === t.key ? "active" : ""}`}
                onClick={() => setActiveTab(t.key)}
              >
                <t.Icon size={16} strokeWidth={2.2} />
                <span>{t.label}</span>
              </button>
            ))}
          </div>

          {/* ══════════════════ TAB: DAY ══════════════════ */}
          {activeTab === "day" && (
            <section className="tab-section">
              <SectionHeader
                icon={CalendarDays}
                title={`Performance on ${dayInView}`}
                note="All times shown are Indian Standard Time (IST)."
              />
              <div className="kpi-grid">
                <KpiCard
                  icon={Wallet}
                  label={<>Total Revenue <InfoHint text="Total amount collected from customers, not counting GST." /></>}
                  value={`₹ ${fmt(daySummary?.totalRevenue)}`}
                  tone="primary"
                />
                <KpiCard icon={Receipt} label="Total Orders" value={daySummary?.totalOrders ?? 0} />
                <KpiCard
                  icon={TrendingUp}
                  label="Average Bill Value"
                  value={`₹ ${fmt(daySummary?.avgOrderValue)}`}
                  sub="Revenue ÷ number of orders"
                />
              </div>

              <div className="finance-card">
                <h4>Revenue by Hour <span className="chart-tz-note">(IST)</span></h4>
                <div className="chart-wrapper">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={revenueTimeline}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--grid)" />
                      <XAxis dataKey="time" tick={{ fontSize: 11 }} tickFormatter={hourLabel} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip
                        formatter={(v) => [`₹ ${fmt(v)}`, "Revenue"]}
                        labelFormatter={(l) => `${l} IST`}
                      />
                      <Line
                        type="monotone"
                        dataKey="revenue"
                        stroke="#6366f1"
                        strokeWidth={2}
                        dot={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                {revenueTimeline.length === 0 && <p className="empty-state">No orders recorded yet for this day.</p>}
              </div>

              <div className="finance-card">
                <h4>Orders by Hour <span className="chart-tz-note">(IST)</span></h4>
                <div className="chart-wrapper">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={revenueTimeline}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--grid)" />
                      <XAxis dataKey="time" tick={{ fontSize: 11 }} tickFormatter={hourLabel} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                      <Tooltip labelFormatter={(l) => `${l} IST`} />
                      <Bar dataKey="orders" fill="#10b981" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </section>
          )}

          {/* ══════════════════ TAB: PERIOD ══════════════════ */}
          {activeTab === "period" && (
            <section className="tab-section">
              <SectionHeader
                icon={CalendarRange}
                title={`${fromDate} to ${toDate}`}
                note="All dates shown are Indian Standard Time (IST)."
              />
              <div className="kpi-grid">
                <KpiCard icon={Wallet} label="Revenue in this Range" value={`₹ ${fmt(periodSummary?.totalRevenue)}`} tone="primary" />
                <KpiCard icon={Receipt} label="Total Orders" value={periodSummary?.totalOrders ?? 0} />
                <KpiCard icon={TrendingUp} label="Average Bill Value" value={`₹ ${fmt(periodSummary?.avgOrderValue)}`} />
              </div>

              {dailyComparison.length === 0 ? (
                <p className="empty-state">No orders recorded yet for this date range.</p>
              ) : (
                <div className="finance-grid">
                  <div className="finance-card">
                    <h4>Revenue by Date</h4>
                    <div className="chart-wrapper">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={dailyComparison}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--grid)" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v) => [`₹ ${fmt(v)}`, "Revenue"]} />
                          <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                            {dailyComparison.map((_, i) => (
                              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="finance-card">
                    <h4>Orders by Date</h4>
                    <div className="chart-wrapper">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={dailyComparison}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--grid)" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                          <Tooltip />
                          <Line type="monotone" dataKey="orders" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="finance-card">
                    <h4>Average Bill Value by Date</h4>
                    <div className="chart-wrapper">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={dailyComparison}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--grid)" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip formatter={(v) => [`₹ ${fmt(v)}`, "Avg bill"]} />
                          <Line type="monotone" dataKey="aov" stroke="#ec4899" strokeWidth={2} dot={{ r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              )}
            </section>
          )}

          {/* ══════════════════ TAB: SALES BREAKDOWN ══════════════════ */}
          {activeTab === "sales" && sb && (
            <section className="tab-section">
              <SectionHeader
                icon={ShoppingBasket}
                title={`What Sold — ${fromDate} to ${toDate}`}
              />

              <div className="breakdown-grid">
                <div className="finance-card">
                  <h4>Revenue by Meal Type</h4>
                  <div className="chart-wrapper">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={sb.menuCategorySales}
                          dataKey="revenue"
                          nameKey="name"
                          cx="50%" cy="50%"
                          outerRadius={90}
                          label={({ name, percent }) =>
                            `${name} ${(percent * 100).toFixed(0)}%`}
                        >
                          {sb.menuCategorySales.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(v) => `₹ ${fmt(v)}`} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                </div>

                <div className="finance-card">
                  <h4>Revenue by Dish Category</h4>
                  <div className="chart-wrapper">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={sb.dishCategorySales} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--grid)" />
                        <XAxis
                          type="number"
                          tick={{ fontSize: 11 }}
                          tickFormatter={(v) => `₹${(v / 1000).toFixed(0)}k`}
                        />
                        <YAxis dataKey="name" type="category" width={110} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => [`₹ ${fmt(v)}`, "Revenue"]} />
                        <Bar dataKey="revenue" radius={[0, 4, 4, 0]}>
                          {sb.dishCategorySales.map((_, i) => (
                            <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>

              <div className="cat-summary-row">
                {sb.menuCategorySales.map((c, i) => (
                  <div
                    className="cat-card"
                    key={c.name}
                    style={{ borderLeft: `4px solid ${CHART_COLORS[i % CHART_COLORS.length]}` }}
                  >
                    <p className="cat-name">{c.name}</p>
                    <p className="cat-rev">₹ {fmt(c.revenue)}</p>
                    <p className="cat-meta">{c.qty} items · {c.orders} orders</p>
                  </div>
                ))}
              </div>

              <div className="finance-card" style={{ marginTop: "1.5rem" }}>
                <h4><Trophy size={15} strokeWidth={2.2} className="h4-icon" /> Top 10 Items by Revenue</h4>
                <RankedTable rows={sb.topItems} valueKey="revenue" label="Revenue" />
              </div>

              <div className="finance-card" style={{ marginTop: "1.5rem" }}>
                <h4><ClipboardList size={15} strokeWidth={2.2} className="h4-icon" /> All Items Sold</h4>
                <p className="scroll-hint">Swipe sideways to see all columns →</p>
                <div className="item-table-wrap">
                  <table className="item-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Item Name</th>
                        <th>Meal Time</th>
                        <th>Category</th>
                        <th>Qty Sold</th>
                        <th>Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sb.itemSales.map((item, i) => (
                        <tr key={item.name} className={i % 2 === 0 ? "row-even" : ""}>
                          <td className="td-rank">{i + 1}</td>
                          <td className="td-name">{item.name}</td>
                          <td><span className="tag tag-meal">{item.menuCat}</span></td>
                          <td><span className="tag tag-cat">{item.dishCat}</span></td>
                          <td className="td-qty">{item.qty}</td>
                          <td className="td-rev">₹ {fmt(item.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </section>
          )}

          {/* ══════════════════ TAB: ITEM TREND ══════════════════ */}
          {activeTab === "trend" && sb && (
            <section className="tab-section">
              <SectionHeader icon={TrendingUp} title="See How One Item Is Selling" />

              <div className="item-trend-selector">
                <label className="trend-label" htmlFor="fs-item-select">Choose an item</label>
                <select
                  id="fs-item-select"
                  className="trend-select"
                  value={selectedItem}
                  onChange={(e) => setSelectedItem(e.target.value)}
                >
                  <option value="">— select an item —</option>
                  {allItemNames.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>

              {!selectedItem && (
                <p className="empty-state">Pick an item above to see how it has been selling.</p>
              )}
              {selectedItem && trendData.length === 0 && (
                <p className="empty-state">No sales found for "{selectedItem}" in this period.</p>
              )}

              {selectedItem && trendData.length > 0 && (
                <>
                  <div className="kpi-grid" style={{ marginTop: "1rem" }}>
                    <KpiCard icon={ShoppingBasket} label="Item" value={selectedItem} />
                    <KpiCard icon={Receipt} label="Total Qty Sold" value={trendItemTotal.qty} />
                    <KpiCard icon={Wallet} label="Total Revenue" value={`₹ ${fmt(trendItemTotal.revenue)}`} tone="primary" />
                    <KpiCard icon={Clock3} label="Days It Sold" value={trendData.length} />
                  </div>

                  <div className="finance-card" style={{ marginTop: "1rem" }}>
                    <h4>Daily Revenue — {selectedItem}</h4>
                    <div className="chart-wrapper">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={trendData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--grid)" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip
                            formatter={(v) => [`₹ ${fmt(v)}`, "Revenue"]}
                            labelFormatter={(l) => `Date: ${l} (IST)`}
                          />
                          <Line
                            type="monotone"
                            dataKey="revenue"
                            stroke="#6366f1"
                            strokeWidth={2.5}
                            dot={{ r: 4, fill: "#6366f1", strokeWidth: 0 }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="finance-card">
                    <h4>Daily Quantity Sold — {selectedItem}</h4>
                    <div className="chart-wrapper">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={trendData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--grid)" />
                          <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                          <Tooltip
                            formatter={(v) => [v, "Qty"]}
                            labelFormatter={(l) => `Date: ${l} (IST)`}
                          />
                          <Bar dataKey="qty" radius={[4, 4, 0, 0]}>
                            {trendData.map((_, i) => (
                              <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="finance-card">
                    <h4>Day-by-Day Breakdown</h4>
                    <div className="item-table-wrap">
                      <table className="item-table">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Date (IST)</th>
                            <th>Qty Sold</th>
                            <th>Revenue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {trendData.map((row, i) => (
                            <tr key={row.date} className={i % 2 === 0 ? "row-even" : ""}>
                              <td className="td-rank">{i + 1}</td>
                              <td className="td-name">{row.date}</td>
                              <td className="td-qty">{row.qty}</td>
                              <td className="td-rev">₹ {fmt(row.revenue)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
};

export default FinanceStats;